/** The itinerary planner's status of one catalogue product (`row.ai`, one query per page): the
 *  latest run's outcome — and, when it succeeded, whether its plan was imported — plus the flags
 *  of the latest ACCEPTED plan (stops merged, season notes, clock times dropped). Shown in the
 *  grid's "Itinerary / AI" column and on the product drawer's Runs tab. A product without a
 *  planner run (`ai: null`) shows nothing; a plan older than the counters shows no flags. */
import { Box, Chip, Link, Stack, Tooltip, Typography } from '@mui/material';

import type { CatalogueAiPlan, CatalogueAiStatus } from '@/api/catalogue';
import { formatDate, formatRelativeTime } from '@/utils/format';

import { aiPlanFlags, aiRunLabel, aiRunTone } from './catalogueModel';
import { triggerLabel } from './RunsTable';

const CHIP_SX = { height: 20, fontSize: 11 };

function runTitle(ai: CatalogueAiStatus): string {
  const outcome =
    ai.status === 'ok' ? (ai.accepted ? 'ok, plan imported' : 'ok, plan not imported') : aiRunLabel(ai);
  const plan = !ai.plan
    ? ' No plan accepted yet.'
    : ai.plan.run_id !== ai.run_id
      ? ` Latest accepted plan: run #${ai.plan.run_id}, ${formatDate(ai.plan.ts)}.`
      : '';
  return `Latest planner run #${ai.run_id} (${triggerLabel(ai.trigger)}, ${formatDate(ai.ts)}): ${outcome}.${plan}`;
}

function AiPlanFlagChips({ plan }: { plan: CatalogueAiPlan | null }) {
  if (!plan) return null;
  return (
    <>
      {aiPlanFlags(plan).map((f) => (
        <Tooltip key={f.key} describeChild title={`${f.help} Accepted plan of run #${plan.run_id}.`}>
          <Chip size="small" variant="outlined" color="info" label={f.label} sx={CHIP_SX} />
        </Tooltip>
      ))}
    </>
  );
}

/** The run chip and the accepted plan's flag chips; nothing without a planner run. */
export function AiStatusChips({ ai }: { ai: CatalogueAiStatus | null }) {
  if (!ai) return null;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      <Tooltip describeChild title={runTitle(ai)}>
        <Chip size="small" variant="outlined" color={aiRunTone(ai)} label={`AI ${aiRunLabel(ai)}`} sx={CHIP_SX} />
      </Tooltip>
      <AiPlanFlagChips plan={ai.plan} />
    </Stack>
  );
}

/** One line for the product drawer: the chips, plus links to the runs they come from. */
export function AiStatusSummary({
  ai,
  onOpenRun,
}: {
  ai: CatalogueAiStatus | null;
  onOpenRun: (runId: number) => void;
}) {
  const runLink = (id: number) => (
    <Link component="button" type="button" variant="caption" onClick={() => onOpenRun(id)} sx={{ verticalAlign: 'baseline' }}>
      #{id}
    </Link>
  );
  return (
    <Box role="group" aria-label="Itinerary planner" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Itinerary planner
      </Typography>
      {!ai ? (
        <Typography variant="body2" color="text.secondary">
          no run yet
        </Typography>
      ) : (
        <>
          <AiStatusChips ai={ai} />
          <Typography variant="caption" color="text.secondary">
            latest run {runLink(ai.run_id)} · {triggerLabel(ai.trigger)} ·{' '}
            <span title={formatDate(ai.ts)}>{formatRelativeTime(ai.ts)}</span>
            {!ai.plan ? (
              ' · no plan accepted yet'
            ) : ai.plan.run_id !== ai.run_id ? (
              <> · accepted plan from run {runLink(ai.plan.run_id)}</>
            ) : null}
          </Typography>
        </>
      )}
    </Box>
  );
}
